/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useState } from 'react';

import moment from 'moment-timezone';

import { FormattedMessage } from 'js/lib/coursera.react-intl';
import deferToClientSideRender from 'js/lib/deferToClientSideRender';
import user from 'js/lib/user';
import { hasEqualUTCOffset } from 'js/utils/DateTimeUtils';

import { Button } from '@coursera/cds-core';
import { WarningFilledIcon } from '@coursera/cds-icons';

import localStorageEx from 'bundles/common/utils/localStorageEx';
import Modal from 'bundles/phoenix/components/Modal';

import _t from 'i18n!nls/page';

const styles = {
  modalContent: css`
    padding: var(--cds-spacing-400) var(--cds-spacing-200) var(--cds-spacing-200);
    font-size: 12px;
    width: 100%;
  `,
  warningIcon: css`
    margin-right: var(--cds-spacing-200);
    margin-top: var(--cds-spacing-100);
  `,
  modalMessage: css`
    display: flex;
  `,
  actions: css`
    margin-top: var(--cds-spacing-200);
    display: inline-block;
    white-space: nowrap;
    width: 100%;
  `,
  changeButton: css`
    margin-right: var(--cds-spacing-100);
  `,
};

const TimezoneMismatchNotification = ({ isItemPage = false }) => {
  const [hasDismissedModal, setHasDismissedModal] = useState(false);
  const courseraSettingsTimezone = user.get().timezone;
  const localTimezone = moment.tz.guess();
  const areUTCOffsetsEqual = hasEqualUTCOffset(courseraSettingsTimezone, localTimezone);

  const shouldHidePopupBasedOnBehavior =
    areUTCOffsetsEqual ||
    hasDismissedModal ||
    localStorageEx.getItem('ignoreTimezoneMismatch', (value) => value === 'true', false);
  const shouldHidePopupBecauseOfPageType = isItemPage;
  if (shouldHidePopupBasedOnBehavior || shouldHidePopupBecauseOfPageType) {
    return null;
  }

  const handleDismiss = () => {
    setHasDismissedModal(true);
  };

  const handleDoNotShowAgain = () => {
    localStorageEx.setItem('ignoreTimezoneMismatch', 'true', (value) => value);
    setHasDismissedModal(true);
  };

  return (
    <div className="rc-TimezoneMismatchNotification">
      <Modal
        className="top-right"
        type="popup"
        trackingName="timezone_mismatch_modal"
        handleClose={handleDismiss}
        modalName={_t('Timezone mismatch warning modal')}
      >
        <div css={styles.modalContent} data-testid="timezone-modal-content">
          <div css={styles.modalMessage}>
            <span css={styles.warningIcon}>
              <WarningFilledIcon size="large" />
            </span>
            <div>
              <FormattedMessage
                message={_t(
                  `Your computer's timezone does not match your Coursera account's timezone setting of {courseraSettingsTimezone}, so deadlines may not be displayed in the timezone you expect.`
                )}
                courseraSettingsTimezone={courseraSettingsTimezone}
              />
              <div css={styles.actions}>
                <Button
                  variant="secondary"
                  size="small"
                  component="a"
                  href="/account-settings"
                  target="_blank"
                  rel="noopener noreferrer"
                  css={styles.changeButton}
                >
                  {_t('Change my timezone setting')}
                </Button>
                <Button variant="ghost" size="small" component="div" onClick={handleDoNotShowAgain}>
                  {_t("Don't show this again")}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default deferToClientSideRender(TimezoneMismatchNotification);
